import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const PRIMARY = [
  { to: "/chat",         label: "Chat" },
  { to: "/deadlines",    label: "Deadlines" },
  { to: "/estimator",    label: "Estimator" },
  { to: "/scholarships", label: "Scholarships" },
];

// New agentic features — additive entries, all free and public.
const NEW_AGENTIC = [
  { to: "/app",              label: "AidWaive Plan (overview)" },
  { to: "/app/saver",        label: "Money Saver Guide" },
  { to: "/app/dream-school", label: "Dream School Rescue" },
  { to: "/app/calendar",     label: "Financial Aid Calendar" },
  { to: "/app/fafsa",        label: "FAFSA Walkthrough" },
];

const MORE = [
  { to: "/journey",       label: "The Journey" },
  { to: "/netprice",      label: "Net Price Calculator" },
  { to: "/find",          label: "Find Schools (AI)" },
  { to: "/sidebyside",    label: "Side-by-Side View" },
  { to: "/decoder",       label: "Aid Letter Decoder" },
  { to: "/compare",       label: "School Comparer" },
  { to: "/appeal",        label: "Appeal Writer" },
  { to: "/loans",         label: "Loan Reality Check" },
  { to: "/glossary",      label: "Plain-English Glossary" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
    <header className="nav-frost sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img
            src="/aidwaive-logo.png"
            alt="AidWaive"
            className="h-10 sm:h-11 w-auto object-contain select-none"
            draggable={false}
          />
        </Link>

        {/* Desktop */}
        <nav className="hidden md:flex items-center gap-7" data-testid="nav-desktop">
          {PRIMARY.map(t => (
            <NavLink key={t.to} to={t.to} data-testid={`nav-${t.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm transition-colors font-medium ${isActive ? "text-gray-900" : "text-gray-700 hover:text-gray-900"}`
              }>
              {t.label}
            </NavLink>
          ))}
          <div className="relative" ref={moreRef}>
            <button
              type="button"
              onClick={() => setMoreOpen(v => !v)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              className="text-sm font-medium text-gray-700 hover:text-gray-900 inline-flex items-center gap-1 transition-colors"
              data-testid="nav-more">
              More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <div role="menu"
                className="absolute right-0 top-full mt-2 w-72 py-2 bg-white border border-gray-200 rounded-lg shadow-lg fade-in"
                data-testid="nav-more-menu">
                <div className="px-4 pt-1 pb-2 text-[10px] uppercase tracking-[0.18em] text-[#1d4ed8]">
                  New — your AidWaive plan
                </div>
                {NEW_AGENTIC.map(t => (
                  <NavLink key={t.to} to={t.to}
                    onClick={() => setMoreOpen(false)}
                    className="block px-4 py-2.5 text-sm text-gray-900 hover:bg-[#eff6ff]"
                    data-testid={`nav-more-${t.to.replace(/\//g, "-").slice(1)}`}>
                    {t.label}
                  </NavLink>
                ))}
                <div className="my-2 border-t border-gray-100" />
                <div className="px-4 py-1 text-[10px] uppercase tracking-[0.18em] text-gray-400">
                  Free tools
                </div>
                {MORE.map(t => (
                  <NavLink key={t.to} to={t.to}
                    onClick={() => setMoreOpen(false)}
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    data-testid={`nav-more-${t.to.slice(1)}`}>
                    {t.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/chat"
            data-testid="nav-cta"
            className="inline-flex items-center justify-center px-4 h-9 rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
            style={{ background: "#1d4ed8", color: "#ffffff" }}>
            Try it free →
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="md:hidden p-2 -mr-2 text-gray-700 active:text-gray-900 touch-manipulation"
          onClick={() => setOpen(v => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="nav-mobile-drawer"
          data-testid="nav-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
    </header>

    {/* Mobile drawer, rendered OUTSIDE the header because the header uses
        backdrop-filter which would otherwise establish a containing block
        and break `position: fixed` on small screens. */}
    {open && (
      <div
        id="nav-mobile-drawer"
        role="dialog"
        aria-modal="true"
        className="md:hidden fixed inset-0 bg-white overflow-y-auto fade-in"
        style={{ zIndex: 60 }}
        data-testid="nav-mobile-drawer">
        <div className="h-14 px-4 sm:px-6 flex items-center justify-between border-b border-gray-200">
          <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2" data-testid="mobile-logo">
            <img src="/aidwaive-logo.png" alt="AidWaive" className="h-5 w-auto" draggable={false} />
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="p-2 -mr-2 text-gray-700 touch-manipulation"
            data-testid="nav-mobile-close">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="px-5 py-6 space-y-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#1d4ed8] mb-3">New — your AidWaive plan</div>
            <div className="space-y-1">
              {NEW_AGENTIC.map(t => (
                <Link key={t.to} to={t.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile${t.to.replace(/\//g, "-")}`}
                  className="block py-3 text-base text-gray-900 border-b border-gray-200">
                  {t.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-gray-400 mb-3">Free tools</div>
            <div className="space-y-1">
              {[...PRIMARY, ...MORE].map(t => (
                <Link key={t.to} to={t.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${t.to.slice(1)}`}
                  className="block py-3 text-base text-gray-900 border-b border-gray-200">
                  {t.label}
                </Link>
              ))}
            </div>
          </div>
          <Link
            to="/chat"
            onClick={() => setOpen(false)}
            data-testid="mobile-cta"
            className="inline-flex items-center justify-center w-full h-11 rounded-md text-sm font-medium"
            style={{ background: "#1d4ed8", color: "#ffffff" }}>
            Try it free →
          </Link>
        </div>
      </div>
    )}
    </>
  );
}
